import * as yup from "yup";
import config from "../../../config";
const constants = require("domuso-constants");

const phoneNumberRegex = /^([+]?(\d{1}[2-9]{1}|\d{2}[2-9]{1})[-\s]?|)\d{2}[-\s]?\d{3}[-\s]?\d{4}$/;
const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const loginSchema = yup.object().shape({
  username: yup.string().trim().required("Email (or username) is required"),
  password: yup.string().required("Password is required"),
});

export const signupSchema = yup.object().shape({
  prop_id: yup.mixed().required("Property is required"),
  first_name: yup.string().trim().required("First name is required"),
  last_name: yup.string().trim().required("Last name is required"),
  email: yup.string().email().required("Email address is required"),
  phone: yup
    .string()
    .matches(phoneNumberRegex, "Invalid phone number")
    .required("Phone number is required"),
});

export const serviceRequestSchema = yup.object().shape({
  category: yup.string().trim().required("Category is required"),
  is_household_sick: yup.boolean().required("You must select an option"),
  has_permission: yup.boolean().required("You must select an option"),
  phone: yup
    .string()
    .trim()
    .matches(phoneNumberRegex, "Invalid phone number")
    .required("Phone number is required"),
  description: yup.string().trim().required("Description is required"),
});

export const yardiServiceRequestSchema = yup.object().shape({
  category: yup.string().trim().required("Category is required"),
  subcategory: yup.string().trim(),
  is_household_sick: yup.boolean().required("You must select an option"),
  has_permission: yup.boolean().required("You must select an option"),
  has_pets: yup.boolean().required("You must select an option"),
  phone: yup
    .string()
    .trim()
    .matches(phoneNumberRegex, "Invalid phone number")
    .required("Phone number is required"),
  brief_description: yup.string().trim().required("Please describe your issue in brief"),
  description: yup.string().trim().required("Please describe your issue"),
});

const paymentMaxAmount = Number(config.paymentMaxAmount);
export const enterPaymentDetailsSchema = ({ allowPartialRentPayments, balanceDue }: any) => {
  return yup.object().shape({
    paymentAmount: yup
      .number()
      .required()
      .test("Min value validation", "Min value validation error", (value, testContext) => {
        if (value === 0 || value == null) {
          return testContext.createError({ message: "Payment amount is required" });
        }

        if (!allowPartialRentPayments && value < balanceDue) {
          return testContext.createError({
            message:
              "Your property does not allow partial payments. Please pay your balance in full",
          });
        }

        return true;
      })
      .max(paymentMaxAmount, `Amount must be less than $${paymentMaxAmount}`),
  });
};

export const reviewCCPaymentSchema = yup.object().shape({
  cof: yup.string(),
  cancellationRefund: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
});

export const reviewCertifiedCCPaymentSchema = yup.object().shape({
  cof: yup.string(),
  cancellationRefund: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
  returnWarning: yup.string().test("item must be checked", "Required", (val) => val === "checked"),
});

export const reviewCertifiedBAPaymentSchema = yup.object().shape({
  cof: yup.string(),
  returnWarning: yup.string().test("item must be checked", "Required", (val) => val === "checked"),
});

export const autoPayDetailsSchema = ({ minimumDate, maximumDate }: any) => {
  return yup.object().shape({
    paymentDate: yup
      .date()
      .required("Please select a date")
      .min(minimumDate, `Must be later than ${minimumDate}`)
      .max(maximumDate, `Must be before ${maximumDate}`),
  });
};

export const checkInfoSchema = (limit: number) => {
  const { PERSONAL, CERTIFIED } = constants.check21.CheckType;
  return yup.object().shape({
    photoFront: yup.string().required("Front image required"),
    photoBack: yup.string().required("Back image required"),
    amount: yup
      .number()
      .required()
      .moreThan(0, "Amount must be greater than 0")
      .max(limit, `Amount must be less than $${limit}`),
    checkType: yup.mixed().oneOf([PERSONAL, CERTIFIED], "Please select a check type"),
    routingNumber: yup
      .string()
      .required("Routing number is required")
      .matches(/^\d+$/, "Routing number must only contain numbers")
      .test("AllZeroes", "Number cannot be all 0's", (val = "") => /[1-9]/.test(`${val}`))
      .test("9Digits", "Please enter 9 digits.", (val = "") => val?.length === 9),
    accountNumber: yup
      .string()
      .required("Account number is required")
      .matches(/^\d+$/, "Account number must only contain numbers")
      .test("AllZeroes", "Number cannot be all 0's", (val = "") => /[1-9]/.test(`${val}`))
      .min(5, "Account number must have at least 5 digits")
      .max(20, "Account number must have a maximum of 20 digits"),
  });
};

export const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .matches(emailRegex, "Invalid email")
    .required("Email or Username is required"),
});

export const changePasswordSchema = yup.object().shape({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: yup
    .string()
    .matches(/^.{8,}$/, "Please enter at least 8 characters.")
    .required("Password is required"),
  confirmNewPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), undefined], "Passwords do not match")
    .required("Confirm New Password is required"),
});

export const bankAccountNumberConfirmationSchema = yup.object().shape({
  manualBankAccountNumber: yup
    .string()
    .matches(/^[0-9]*$/, "Only numeric characters are allowed.")
    .matches(/^(?!0+$)/, "Account number cannot contain all zeroes.")
    .min(5, "Please enter at least 5 characters.")
    .max(17, "Please enter maximum of 17 characters.")
    .trim()
    .required("Account number is required")
    .nullable(),
  manualReBankAccountNumber: yup
    .string()
    .matches(/^[0-9]*$/, "Only numeric characters are allowed.")
    .matches(/^(?!0+$)/, "Account number cannot contain all zeroes.")
    .min(5, "Please enter at least 5 characters.")
    .max(17, "Please enter maximum of 17 characters.")
    .trim()
    .oneOf([yup.ref("manualBankAccountNumber"), undefined], "Account numbers do not match")
    .required("Re-Account number is required")
    .nullable(),
});
